// src/components/Machine.js

import { useState, useEffect } from 'react';
import { supabase } from '../services/supabase';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import Select from 'react-select';
import Modal from 'react-modal';

// Import product images
import vf5Image from '../assets/vf5.png';
import vf4Image from '../assets/vf4.png';
import vf2Image from '../assets/vf2.png';
import hardinge from '../assets/hardinge.png';
import vm2 from '../assets/vm2.png';
import bridgeport from '../assets/bridgeport.png';

// Bind modal to app element for accessibility
Modal.setAppElement('#root');

function Machine() {

  document.title ='Machines';

  const { user } = useAuth();
  const [machines, setMachines] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [shops, setShops] = useState({});
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedMachine, setSelectedMachine] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!user) return;

      setLoading(true);
      try {
        // Fetch all machines with shop_id, current_status, capacity_per_day, and current_job_id
        const { data: machinesData, error: machinesError } = await supabase
          .from('machines')
          .select('id, name, shop_id, current_status, capacity_per_day, current_job_id');
        if (machinesError) throw new Error(`Error fetching machines: ${machinesError.message}`);
        setMachines(machinesData || []);

        // Fetch all jobs (exclude closed jobs)
        const { data: jobsData, error: jobsError } = await supabase
          .from('jobs')
          .select('id, po_number, po_line_number, part_number, status')
          .neq('status', 'closed')
          .order('po_number');
        if (jobsError) throw new Error(`Error fetching jobs: ${jobsError.message}`);
        setJobs(jobsData || []);

        // Fetch shops to map shop_id to shop name
        const { data: shopsData, error: shopsError } = await supabase
          .from('shops')
          .select('id, name');
        if (shopsError) throw new Error(`Error fetching shops: ${shopsError.message}`);
        const shopMap = shopsData.reduce((map, shop) => {
          map[shop.id] = shop.name || 'Unknown';
          return map;
        }, {});
        setShops(shopMap);
      } catch (err) {
        console.error('Error fetching data:', err);
        toast.error(`Error: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user]);

  const handleSetActiveJob = async (machineId, jobId) => {
    try {
      // If jobId is empty, clear the current job and set status to "Not in Use"
      if (!jobId) {
        const { error: updateError } = await supabase
          .from('machines')
          .update({ current_job_id: null, current_status: 'Not in Use' })
          .eq('id', machineId);
        if (updateError) throw new Error(`Error clearing active job: ${updateError.message}`);

        setMachines(prev =>
          prev.map(machine =>
            machine.id === machineId
              ? { ...machine, current_job_id: null, current_status: 'Not in Use' }
              : machine
          )
        );

        toast.success('Active job removed successfully!');
        setIsModalOpen(false); // Close the modal
        return;
      }

      // Check if the job is already assigned to another machine
      const { data: existingAssignment, error: checkError } = await supabase
        .from('machines')
        .select('id, name')
        .eq('current_job_id', jobId)
        .limit(1);
      if (checkError) throw new Error(`Error checking existing assignment: ${checkError.message}`);

      if (existingAssignment.length > 0) {
        const assignedMachine = existingAssignment[0];
        toast.error(`This job is already assigned to ${assignedMachine.name || 'another machine'}.`);
        return;
      }

      // Update the machine with the new job and set status to "Active"
      const { error: updateError } = await supabase
        .from('machines')
        .update({ current_job_id: jobId, current_status: 'Active' })
        .eq('id', machineId);
      if (updateError) throw new Error(`Error setting active job: ${updateError.message}`);

      // Update the machines state
      setMachines(prev =>
        prev.map(machine =>
          machine.id === machineId
            ? { ...machine, current_job_id: jobId, current_status: 'Active' }
            : machine
        )
      );

      // Re-fetch jobs to ensure the active job details are up-to-date
      const { data: jobsData, error: jobsError } = await supabase
        .from('jobs')
        .select('id, po_number, po_line_number, part_number, status')
        .neq('status', 'closed')
        .order('po_number');
      if (jobsError) throw new Error(`Error re-fetching jobs: ${jobsError.message}`);
      setJobs(jobsData || []);

      toast.success('Active job set successfully!');
      setIsModalOpen(false); // Close the modal
    } catch (err) {
      console.error('Error setting active job:', err);
      toast.error(`Error: ${err.message}`);
    }
  };

  // Map machine names to their product images
  const getMachineImage = (machineName) => {
    switch (machineName.toLowerCase()) {
      case 'haas vf5':
        return vf5Image;
      case 'haas vf4':
        return vf4Image;
      case 'haas vf2':
        return vf2Image;
      case 'hardinge':
        return hardinge;
        case 'haas vm2':
        return vm2;
        case 'bridgeport':
        return bridgeport;
      default:
        // Fallback image
        return 'https://via.placeholder.com/300x200?text=Machine+Image';
    }
  };

  // Format job options for react-select
  const jobOptions = [
    { value: '', label: 'None' },
    ...jobs.map(job => ({
      value: job.id,
      label: `${job.po_number} - Line ${job.po_line_number} - ${job.part_number}`,
    })),
  ];

  // Separate machines by shop
  const northShopMachines = machines.filter(machine => machine.shop_id === 1);
  const southShopMachines = machines.filter(machine => machine.shop_id === 2);

  // Open the modal for a specific machine
  const openModal = (machine) => {
    setSelectedMachine(machine);
    setIsModalOpen(true);
  };

  // Close the modal
  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedMachine(null);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-8">
      <div className="max-w-7xl mx-auto">

        {/* North Shop Section */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-gray-700 mb-6 border-b-2 border-indigo-200 pb-2">
            North Shop Machines
          </h2>
          {northShopMachines.length === 0 ? (
            <div className="text-center text-gray-500 text-lg">No machines available in North Shop.</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {northShopMachines.map(machine => {
                const activeJob = jobs.find(job => job.id === machine.current_job_id);
                const machineImage = getMachineImage(machine.name);

                return (
                  <div
                    key={machine.id}
                    className="relative bg-white rounded-xl shadow-lg overflow-hidden transform transition-all duration-300 hover:shadow-2xl hover:-translate-y-1"
                  >
                    {/* Product Image */}
                    <div className="relative h-48 w-full">
                      <img
                        src={machineImage}
                        alt={machine.name}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                      <h2 className="absolute bottom-4 left-4 text-2xl font-bold text-white tracking-wide">
                        {machine.name}
                      </h2>
                    </div>

                    {/* Card Content */}
                    <div className="p-6">
                      <div className="flex justify-between items-center mb-4">
                        <div>
                          <p className="text-sm font-medium text-gray-600">Shop</p>
                          <p className="text-lg font-semibold text-gray-800">
                            {shops[machine.shop_id] || 'Unknown'}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-600">Status</p>
                          <span
                            className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                              machine.current_status === 'Active'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {machine.current_status === 'Active' ? 'Active' : 'Not in Use'}
                          </span>
                        </div>
                      </div>

                      <div className="mb-4">
                        <p className="text-sm font-medium text-gray-600">Capacity Per Day</p>
                        <p className="text-lg font-semibold text-gray-800">
                          {machine.capacity_per_day || 'N/A'}
                        </p>
                      </div>

                      <div className="mb-4">
                        <p className="text-sm font-medium text-gray-600">Active Part</p>
                        <p className="text-lg font-semibold text-gray-800">
                          {activeJob
                            ? `${activeJob.part_number}`
                            : 'None'}
                        </p>
                      </div>

                      <div>
                        <button
                          onClick={() => openModal(machine)}
                          className="w-full py-2 px-4 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        >
                          Set Active Job
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* South Shop Section */}
        <div>
          <h2 className="text-3xl font-bold text-gray-700 mb-6 border-b-2 border-indigo-200 pb-2">
            South Shop Machines
          </h2>
          {southShopMachines.length === 0 ? (
            <div className="text-center text-gray-500 text-lg">No machines available in South Shop.</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {southShopMachines.map(machine => {
                const activeJob = jobs.find(job => job.id === machine.current_job_id);
                const machineImage = getMachineImage(machine.name);

                return (
                  <div
                    key={machine.id}
                    className="relative bg-white rounded-xl shadow-lg overflow-hidden transform transition-all duration-300 hover:shadow-2xl hover:-translate-y-1"
                  >
                    {/* Product Image */}
                    <div className="relative h-48 w-full">
                      <img
                        src={machineImage}
                        alt={machine.name}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                      <h2 className="absolute bottom-4 left-4 text-2xl font-bold text-white tracking-wide">
                        {machine.name}
                      </h2>
                    </div>

                    {/* Card Content */}
                    <div className="p-6">
                      <div className="flex justify-between items-center mb-4">
                        <div>
                          <p className="text-sm font-medium text-gray-600">Shop</p>
                          <p className="text-lg font-semibold text-gray-800">
                            {shops[machine.shop_id] || 'Unknown'}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-600">Status</p>
                          <span
                            className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                              machine.current_status === 'Active'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {machine.current_status === 'Active' ? 'Active' : 'Not in Use'}
                          </span>
                        </div>
                      </div>

                      <div className="mb-4">
                        <p className="text-sm font-medium text-gray-600">Capacity Per Day</p>
                        <p className="text-lg font-semibold text-gray-800">
                          {machine.capacity_per_day || 'N/A'}
                        </p>
                      </div>

                      <div className="mb-4">
                        <p className="text-sm font-medium text-gray-600">Active Part</p>
                        <p className="text-lg font-semibold text-gray-800">
                          {activeJob
                            ? `${activeJob.part_number}`
                            : 'None'}
                        </p>
                      </div>

                      <div>
                        <button
                          onClick={() => openModal(machine)}
                          className="w-full py-2 px-4 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        >
                          Set Active Job
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Modal for Setting Active Job */}
        {selectedMachine && (
          <Modal
            isOpen={isModalOpen}
            onRequestClose={closeModal}
            className="bg-white rounded-xl shadow-2xl max-w-lg w-full mx-auto my-8 p-0"
            overlayClassName="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center"
            style={{
              overlay: {
                zIndex: 1000, // Ensure the overlay is above other elements
              },
              content: {
                overflow: 'visible', // Allow overflow for the dropdown
              },
            }}
          >
            <div className="relative">
              {/* Product Image */}
              <div className="relative h-48 w-full">
                <img
                  src={getMachineImage(selectedMachine.name)}
                  alt={selectedMachine.name}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                <h2 className="absolute bottom-4 left-4 text-2xl font-bold text-white tracking-wide">
                  {selectedMachine.name}
                </h2>
                <button
                  onClick={closeModal}
                  className="absolute top-4 right-4 text-white hover:text-gray-300 focus:outline-none"
                >
                  <svg
                    className="h-6 w-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Shop</p>
                    <p className="text-lg font-semibold text-gray-800">
                      {shops[selectedMachine.shop_id] || 'Unknown'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600">Status</p>
                    <span
                      className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                        selectedMachine.current_status === 'Active'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {selectedMachine.current_status === 'Active' ? 'Active' : 'Not in Use'}
                    </span>
                  </div>
                </div>

                <div className="mb-4">
                  <p className="text-sm font-medium text-gray-600">Capacity Per Day</p>
                  <p className="text-lg font-semibold text-gray-800">
                    {selectedMachine.capacity_per_day || 'N/A'}
                  </p>
                </div>

                <div className="mb-6">
                  <p className="text-sm font-medium text-gray-600">Current Active Job</p>
                  <p className="text-lg font-semibold text-gray-800">
                    {jobs.find(job => job.id === selectedMachine.current_job_id)
                      ? `${jobs.find(job => job.id === selectedMachine.current_job_id).po_number} - Line ${
                          jobs.find(job => job.id === selectedMachine.current_job_id).po_line_number
                        } - ${jobs.find(job => job.id === selectedMachine.current_job_id).part_number}`
                      : 'None'}
                  </p>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-600 block mb-1">Set Active Job</label>
                  <Select
                    options={jobOptions}
                    value={jobOptions.find(option => option.value === (selectedMachine.current_job_id || ''))}
                    onChange={(selectedOption) =>
                      handleSetActiveJob(selectedMachine.id, selectedOption ? selectedOption.value : '')
                    }
                    placeholder="Search for a job..."
                    className="w-full"
                    classNamePrefix="select"
                    isClearable
                    menuPortalTarget={document.body} // Render the dropdown menu outside the modal
                    styles={{
                      control: (provided) => ({
                        ...provided,
                        borderColor: '#d1d5db',
                        boxShadow: 'none',
                        '&:hover': {
                          borderColor: '#a5b4fc',
                        },
                      }),
                      option: (provided, state) => ({
                        ...provided,
                        backgroundColor: state.isSelected ? '#4f46e5' : state.isFocused ? '#e0e7ff' : 'white',
                        color: state.isSelected ? 'white' : '#1f2937',
                        '&:hover': {
                          backgroundColor: '#e0e7ff',
                          color: '#1f2937',
                        },
                      }),
                      menu: (provided) => ({
                        ...provided,
                        borderRadius: '0.5rem',
                        marginTop: '0.25rem',
                        zIndex: 9999, // Ensure the dropdown menu is above the modal
                      }),
                      menuPortal: (provided) => ({
                        ...provided,
                        zIndex: 9999, // Ensure the portal has a high z-index
                      }),
                    }}
                  />
                </div>
              </div>
            </div>
          </Modal>
        )}
      </div>
    </div>
  );
}

export default Machine;